// API Types
export interface Tariff {
  id: number;
  TARIF: string;
  VERSICHERUNG: string;
  thanksPage: string;
  automationProvider: string;
  automationProviderSettings: {
    templateId: {
      SAME: string;
      DIFFERENT: string;
      DIFFERENT_EMAIL_KIDS: string;
      DIFFERENT_POST_KIDS: string;
    };
  };
  documensoTemplates?: {
    SAME?: string;
    DIFFERENT?: string;
    DIFFERENT_EMAIL_KIDS?: string;
    DIFFERENT_POST_KIDS?: string;
  };
  PrintOffsets?: PrintOffsets;
  isActive?: boolean | null;
  enabled?: boolean | null;
  aliasTariff?: { id: string; title: string; vendor: string } | null;
}

export interface PrintOffset {
  left: number;
  top: number;
}

export interface PrintOffsets {
  dateTab: PrintOffset;
  radioTabWimpel: PrintOffset;
  radioTabX: PrintOffset;
  replacedReplacementText: PrintOffset;
  replacementText: PrintOffset;
  signTab: PrintOffset;
  txtTab: PrintOffset;
  locked: PrintOffset;
}

export type VariantType = 'SAME' | 'DIFFERENT' | 'DIFFERENT_EMAIL_KIDS' | 'DIFFERENT_POST_KIDS';

// UI-only variant type: KIDS maps to both DIFFERENT_EMAIL_KIDS and DIFFERENT_POST_KIDS in the DB
export type UIVariantType = 'SAME' | 'DIFFERENT' | 'KIDS';

export const UI_VARIANTS: UIVariantType[] = ['SAME', 'DIFFERENT', 'KIDS'];

export const UI_VARIANT_LABELS: Record<UIVariantType, string> = {
  SAME: 'Same Person',
  DIFFERENT: 'Different Person',
  KIDS: 'Kids',
};

/** Maps a UI variant to the primary DB key used for storage/loading */
export function uiVariantToDbKey(uiVariant: UIVariantType): VariantType {
  if (uiVariant === 'KIDS') return 'DIFFERENT_EMAIL_KIDS';
  return uiVariant;
}

/** Maps a UI variant to all DB keys it represents (for saving) */
export function uiVariantToDbKeys(uiVariant: UIVariantType): VariantType[] {
  if (uiVariant === 'KIDS') return ['DIFFERENT_EMAIL_KIDS', 'DIFFERENT_POST_KIDS'];
  return [uiVariant];
}

/** Maps a DB variant key back to its UI variant */
export function dbKeyToUiVariant(dbKey: VariantType): UIVariantType {
  if (dbKey === 'DIFFERENT_EMAIL_KIDS' || dbKey === 'DIFFERENT_POST_KIDS') return 'KIDS';
  return dbKey;
}

export interface TemplateVersion {
  templateId: string;
  version: number;
  tariffId: number;
  variantType: VariantType;
  status: 'draft' | 'published' | 'inactive';
  s3Key: string;
  createdAt: string;
  createdBy: string;
  publishedAt?: string;
  publishedBy?: string;
}

// DocuSign Template Types
export interface DocuSignTemplate {
  templateId: string;
  uri?: string;
  name: string;
  shared?: string;
  passwordProtected?: string;
  description?: string;
  created?: string;
  lastModified?: string;
  lastModifiedBy?: {
    userName: string;
    userId: string;
    email: string;
    uri?: string;
  };
  lastUsed?: string;
  owner?: {
    userName: string;
    userId: string;
    email: string;
  };
  pageCount: string;
  folderIds?: string[];
  autoMatch?: string;
  autoMatchSpecifiedByUser?: string;
  documents: DocuSignDocument[];
  recipients: DocuSignRecipients;
  textCustomFields?: DocuSignCustomField[];
  listCustomFields?: DocuSignCustomField[];
}

export interface DocuSignDocument {
  documentId: string;
  uri?: string;
  name: string;
  order: string;
  pages: string;
  documentBase64: string;
}

export interface DocuSignRecipients {
  signers: DocuSignSigner[];
}

export interface DocuSignSigner {
  defaultRecipient?: string;
  recipientId: string;
  email?: string;
  name?: string;
  tabs: DocuSignTabs;
}

export interface DocuSignTabs {
  signHereTabs?: DocuSignSignHereTab[];
  textTabs?: DocuSignTextTab[];
  checkboxTabs?: DocuSignCheckboxTab[];
  dateSignedTabs?: DocuSignDateSignedTab[];
  radioGroupTabs?: DocuSignRadioGroupTab[];
}

export interface DocuSignBaseTab {
  name?: string;
  tabLabel: string;
  documentId: string;
  recipientId: string;
  pageNumber: string;
  xPosition: string;
  yPosition: string;
  width?: string;
  height?: string;
}

export interface DocuSignSignHereTab extends DocuSignBaseTab {
  stampType?: string;
  scaleValue?: string;
  optional?: string;
}

export interface DocuSignTextTab extends DocuSignBaseTab {
  value?: string;
  required?: string;
  locked?: string;
  fontSize?: string;
  fontColor?: string;
}

export interface DocuSignCheckboxTab extends DocuSignBaseTab {
  selected?: string;
  required?: string;
  locked?: string;
}

export interface DocuSignDateSignedTab extends DocuSignBaseTab {
  value?: string;
  required?: string;
  locked?: string;
  fontSize?: string;
}

export interface DocuSignRadioGroupTab {
  documentId: string;
  recipientId: string;
  templateLocked?: string;
  templateRequired?: string;
  groupName: string;
  radios: Array<{
    pageNumber: string;
    xPosition: string;
    yPosition: string;
    value: string;
    selected?: string;
    tabId?: string;
    required?: string;
    locked?: string;
    font?: string;
    bold?: string;
    italic?: string;
    underline?: string;
    fontColor?: string;
    fontSize?: string;
  }>;
  shared?: string;
  requireInitialOnSharedChange?: string;
  requireAll?: string;
  tabType?: string;
  value?: string;
  originalValue?: string;
  source?: string;
}

export interface DocuSignCustomField {
  name: string;
  required: string;
  show: string;
  value: string;
}

// Predefined Fields
export interface PredefinedField {
  tabLabel: string;
  displayName: string;
  dataType: 'text' | 'date' | 'number';
  defaultWidth: number;
  defaultHeight: number;
  defaultFontSize: number;
  description: string;
  category: 'antragsteller' | 'versicherte' | 'tarif' | 'other';
  defaultLocked: boolean;
}

export const PREDEFINED_FIELDS: PredefinedField[] = [
  // Antragsteller (Client/Applicant) fields - prefix 'a_'
  { tabLabel: 'a_birthday', displayName: 'Birthday', dataType: 'date', defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12, description: 'Client date of birth', category: 'antragsteller', defaultLocked: true },
  { tabLabel: 'a_city', displayName: 'City', dataType: 'text', defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12, description: 'Client city', category: 'antragsteller', defaultLocked: false },
  { tabLabel: 'a_email', displayName: 'Email', dataType: 'text', defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12, description: 'Client email address', category: 'antragsteller', defaultLocked: false },
  { tabLabel: 'a_firstname', displayName: 'First Name', dataType: 'text', defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12, description: 'Client first name', category: 'antragsteller', defaultLocked: true },
  { tabLabel: 'a_fullname', displayName: 'Full Name', dataType: 'text', defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12, description: 'Client full name', category: 'antragsteller', defaultLocked: true },
  { tabLabel: 'a_lastname', displayName: 'Last Name', dataType: 'text', defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12, description: 'Client last name', category: 'antragsteller', defaultLocked: true },
  { tabLabel: 'a_phone', displayName: 'Phone', dataType: 'text', defaultWidth: 120, defaultHeight: 20, defaultFontSize: 12, description: 'Client phone number', category: 'antragsteller', defaultLocked: false },
  { tabLabel: 'a_street', displayName: 'Street', dataType: 'text', defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12, description: 'Client street address', category: 'antragsteller', defaultLocked: false },
  { tabLabel: 'a_zip', displayName: 'ZIP Code', dataType: 'text', defaultWidth: 80, defaultHeight: 20, defaultFontSize: 12, description: 'Client postal code', category: 'antragsteller', defaultLocked: false },
  
  // Versicherte Person (Insured Person) fields - prefix 'v_'
  { tabLabel: 'v_birthday', displayName: 'Birthday', dataType: 'date', defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12, description: 'Insured person date of birth', category: 'versicherte', defaultLocked: true },
  { tabLabel: 'v_firstname', displayName: 'First Name', dataType: 'text', defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12, description: 'Insured person first name', category: 'versicherte', defaultLocked: true },
  { tabLabel: 'v_fullname', displayName: 'Full Name', dataType: 'text', defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12, description: 'Insured person full name', category: 'versicherte', defaultLocked: true },
  { tabLabel: 'v_lastname', displayName: 'Last Name', dataType: 'text', defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12, description: 'Insured person last name', category: 'versicherte', defaultLocked: true },
  
  // Tarif Daten (Tariff Data) fields
  { tabLabel: 'lead_id', displayName: 'Lead ID', dataType: 'text', defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12, description: 'Lead identifier', category: 'tarif', defaultLocked: true },
  { tabLabel: 'price', displayName: 'Price', dataType: 'number', defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12, description: 'Premium amount', category: 'tarif', defaultLocked: true },
  { tabLabel: 'v_start', displayName: 'Start Date', dataType: 'date', defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12, description: 'Policy start date', category: 'tarif', defaultLocked: true },
];
